(() => {
  const initialiseHeader = () => {
    const header = document.querySelector('.header-wrapper');
    if (!header) return;
    const headerSection = document.querySelector('.section-header');

    const updateHeaderOffset = () => {
      if (!headerSection) return;
      const height = Math.ceil(headerSection.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--tc-header-offset', `${height}px`);
    };

    let ticking = false;
    const updateHeader = () => {
      header.classList.toggle('tc-header--compact', window.scrollY > 24);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateHeader);
      },
      { passive: true }
    );

    window.addEventListener('resize', updateHeaderOffset, { passive: true });
    if ('ResizeObserver' in window && headerSection) {
      new ResizeObserver(updateHeaderOffset).observe(headerSection);
    }

    updateHeaderOffset();
    updateHeader();
  };

  const initialiseArtworkZoom = () => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document.querySelectorAll('[data-tc-zoom]').forEach((frame) => {
      frame.addEventListener('pointermove', (event) => {
        const bounds = frame.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width) * 100;
        const y = ((event.clientY - bounds.top) / bounds.height) * 100;
        frame.style.setProperty('--tc-zoom-x', `${Math.max(0, Math.min(100, x))}%`);
        frame.style.setProperty('--tc-zoom-y', `${Math.max(0, Math.min(100, y))}%`);
      });

      frame.addEventListener('pointerleave', () => {
        frame.style.removeProperty('--tc-zoom-x');
        frame.style.removeProperty('--tc-zoom-y');
      });
    });
  };

  const initialiseProductContact = () => {
    const dialog = document.querySelector('[data-tc-contact-dialog]');
    if (!dialog) return;

    const title = dialog.querySelector('[data-tc-dialog-title]');
    const introduction = dialog.querySelector('[data-tc-dialog-intro]');
    const offerField = dialog.querySelector('[data-tc-offer-field]');
    const offerInput = offerField?.querySelector('input');
    const requestType = dialog.querySelector('[data-tc-request-type]');
    let activeTrigger = null;

    const setMode = (mode) => {
      const offerMode = mode === 'offer';
      if (title) title.textContent = offerMode ? dialog.dataset.offerTitle : dialog.dataset.enquiryTitle;
      if (introduction) introduction.textContent = offerMode ? dialog.dataset.offerText : dialog.dataset.enquiryText;
      if (offerField) offerField.hidden = !offerMode;
      if (offerInput) {
        offerInput.required = offerMode;
        offerInput.disabled = !offerMode;
        if (!offerMode) offerInput.value = '';
      }
      if (requestType) requestType.value = offerMode ? 'Artwork offer' : 'Artwork enquiry';
    };

    const openDialog = (mode = 'enquiry', trigger = null) => {
      activeTrigger = trigger || document.activeElement;
      setMode(mode);
      if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      else dialog.setAttribute('open', '');
      document.body.classList.add('tc-dialog-open');
      window.requestAnimationFrame(() => dialog.querySelector('input:not([type="hidden"]), textarea')?.focus());
    };

    const closeDialog = () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      document.body.classList.remove('tc-dialog-open');
    };

    document.querySelectorAll('[data-tc-inquiry-trigger]').forEach((button) => {
      button.addEventListener('click', () => openDialog('enquiry', button));
    });

    document.querySelectorAll('[data-tc-offer-trigger]').forEach((button) => {
      button.addEventListener('click', () => {
        const appButton = document.querySelector('#ma-make-an-offer-open-button');
        if (appButton) appButton.click();
        else openDialog('offer', button);
      });
    });

    dialog.querySelectorAll('[data-tc-dialog-close]').forEach((button) => {
      button.addEventListener('click', closeDialog);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });
    dialog.addEventListener('close', () => {
      document.body.classList.remove('tc-dialog-open');
      activeTrigger?.focus();
      activeTrigger = null;
    });

    if (dialog.querySelector('[data-tc-contact-result]')) openDialog('enquiry');

    let offerAppObserver;
    const detectOfferApp = () => {
      const appReady = Boolean(document.querySelector('#ma-make-an-offer-open-button'));
      document.body.classList.toggle('tc-offer-app-ready', appReady);
      if (appReady) offerAppObserver?.disconnect();
    };
    detectOfferApp();
    if (!document.body.classList.contains('tc-offer-app-ready')) {
      offerAppObserver = new MutationObserver(detectOfferApp);
      offerAppObserver.observe(document.body, { childList: true, subtree: true });
    }
  };

  const initialisePurchaseBar = () => {
    const bar = document.querySelector('[data-tc-purchase-bar]');
    const product = document.querySelector('.tc-artwork-product');
    if (!bar || !product) return;

    const acquireButton = bar.querySelector('[data-tc-sticky-acquire]');
    acquireButton?.addEventListener('click', () => {
      const submitButton = product.querySelector('[id^="ProductSubmitButton-"]');
      if (submitButton && !submitButton.disabled) submitButton.click();
      else {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        product.querySelector('.tc-artwork-product__commerce')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
    });

    let ticking = false;
    const update = () => {
      const bounds = product.getBoundingClientRect();
      const visible = bounds.top < -180 && bounds.bottom > 120;
      bar.classList.toggle('is-visible', visible);
      document.body.classList.toggle('tc-purchase-bar-visible', visible);
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  };

  const initialiseViewingRoom = () => {
    const dialog = document.querySelector('[data-tc-viewing-dialog]');
    if (!dialog) return;

    const image = dialog.querySelector('[data-tc-viewing-image]');
    const artist = dialog.querySelector('[data-tc-viewing-artist]');
    const title = dialog.querySelector('[data-tc-viewing-title]');
    const details = dialog.querySelector('[data-tc-viewing-details]');
    const price = dialog.querySelector('[data-tc-viewing-price]');
    const link = dialog.querySelector('[data-tc-viewing-link]');
    let activeTrigger = null;

    document.querySelectorAll('[data-tc-viewing-open]').forEach((button) => {
      button.addEventListener('click', () => {
        activeTrigger = button;
        if (image) {
          image.src = button.dataset.image || '';
          image.alt = button.dataset.imageAlt || button.dataset.title || '';
          image.hidden = !button.dataset.image;
        }
        if (artist) artist.textContent = button.dataset.artist || '';
        if (title) title.textContent = button.dataset.title || '';
        if (details) details.textContent = button.dataset.details || '';
        if (price) price.textContent = button.dataset.price || '';
        if (link) link.href = button.dataset.url || '#';
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
        document.body.classList.add('tc-dialog-open');
        window.requestAnimationFrame(() => dialog.querySelector('[data-tc-viewing-close]')?.focus());
      });
    });

    const close = () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      document.body.classList.remove('tc-dialog-open');
    };

    dialog.querySelectorAll('[data-tc-viewing-close]').forEach((button) => button.addEventListener('click', close));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('close', () => {
      document.body.classList.remove('tc-dialog-open');
      activeTrigger?.focus();
      activeTrigger = null;
    });
  };

  const initialiseArtistContact = () => {
    const dialog = document.querySelector('[data-tc-artist-dialog]');
    if (!dialog) return;

    let activeTrigger = null;

    const open = (trigger = null) => {
      activeTrigger = trigger || document.activeElement;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.body.classList.add('tc-dialog-open');
      window.requestAnimationFrame(() => dialog.querySelector('input:not([type="hidden"]), textarea')?.focus());
    };

    const close = () => {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      document.body.classList.remove('tc-dialog-open');
    };

    document.querySelectorAll('[data-tc-artist-inquiry-trigger]').forEach((button) => button.addEventListener('click', () => open(button)));
    dialog.querySelectorAll('[data-tc-artist-dialog-close]').forEach((button) => button.addEventListener('click', close));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) close();
    });
    dialog.addEventListener('close', () => {
      document.body.classList.remove('tc-dialog-open');
      activeTrigger?.focus();
      activeTrigger = null;
    });
    if (dialog.querySelector('[data-tc-artist-contact-result]')) open();
  };

  const initialiseArtistNavigation = () => {
    document.querySelectorAll('[data-tc-artist-nav]').forEach((nav) => {
      if (nav.dataset.tcArtistNavReady === 'true') return;

      const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
      const observed = [];

      links.forEach((link) => {
        const target = document.getElementById(link.getAttribute('href').slice(1));
        if (!target) {
          link.hidden = true;
          return;
        }
        observed.push({ link, target });
      });

      if (!observed.length) {
        nav.hidden = true;
        return;
      }

      nav.dataset.tcArtistNavReady = 'true';

      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (!visible) return;

          observed.forEach(({ link, target }) => {
            if (target === visible.target) link.setAttribute('aria-current', 'location');
            else link.removeAttribute('aria-current');
          });
        },
        { rootMargin: '-24% 0px -62% 0px', threshold: [0, 0.1, 0.35] }
      );

      observed.forEach(({ target }) => observer.observe(target));
    });
  };

  initialiseHeader();
  initialiseArtworkZoom();
  initialiseProductContact();
  initialisePurchaseBar();
  initialiseArtistContact();
  initialiseArtistNavigation();
  initialiseViewingRoom();

  document.addEventListener('shopify:section:load', initialiseArtistNavigation);
})();
